import { Chart, ChartDataset } from "chart.js";
import { IAverageAlertDurations } from "app/models/statistics.model";
import { AVERAGE_ALERT_DURATIONS_CHART_CONFIGS } from "app/shared/chart-configs.shared";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SecondsToReadableFormatPipe } from "app/pipes/pipes";
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectionStrategy,
} from "@angular/core";
import { StatisticsExportMode, StatisticsExportSnapshot, StatisticsExportWidget } from "app/services/statistics-export.models";
import { renderNativeSnapshotToPng } from "app/services/statistics-native-canvas.util";
import { HelperService } from "app/services/helper.service";
import { StatisticsPngDownloadBtnComponent } from "../statistics-png-download-btn/statistics-png-download-btn.component";

@Component({
  selector: "average-alert-durations",
  templateUrl: "./average-alert-durations.component.html",
  styleUrls: ["./average-alert-durations.component.css"],
  imports: [TranslateModule, SecondsToReadableFormatPipe, StatisticsPngDownloadBtnComponent],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AverageAlertDurationsComponent implements OnInit, OnDestroy, StatisticsExportWidget {
  constructor(
    private translate: TranslateService,
    private helper: HelperService
  ) {}

  @Input() durations: IAverageAlertDurations;

  chart: Chart;
  avgTotalDuration: number = 0;

  labels: {
    technicalDurationLabel: string;
    queueTimeLabel: string;
    reservedTimeLabel: string;
    careTimeLabel: string;
  };

  drawAverageAlertDurationChart(): void {
    this.chart?.destroy();

    const datasets: ChartDataset<"bar">[] = [
      {
        label: this.labels.technicalDurationLabel,
        data: [this.durations.avgTechDurationSeconds],
        backgroundColor: Colors.technicalDurationColor,
        barThickness: 35,
      },
      {
        label: this.labels.queueTimeLabel,
        data: [this.durations.avgQueueTimeSeconds],
        backgroundColor: Colors.queueTimeColor,
        barThickness: 35,
      },
      {
        label: this.labels.reservedTimeLabel,
        data: [this.durations.avgReservedTimeSeconds],
        backgroundColor: Colors.reservedTimeColor,
        barThickness: 35,
      },
      {
        label: this.labels.careTimeLabel,
        data: [this.durations.avgCareTimeSeconds],
        backgroundColor: Colors.careTimeColor,
        barThickness: 35,
      },
    ];

    // Build a local config so the shared singleton is never mutated.
    const config = {
      ...AVERAGE_ALERT_DURATIONS_CHART_CONFIGS,
      data: { datasets, labels: [""] },
      options: {
        ...AVERAGE_ALERT_DURATIONS_CHART_CONFIGS.options,
        scales: {
          ...AVERAGE_ALERT_DURATIONS_CHART_CONFIGS.options?.scales,
          x: {
            ...AVERAGE_ALERT_DURATIONS_CHART_CONFIGS.options?.scales?.['x'],
            max: this.avgTotalDuration,
          },
        },
      },
    };

    this.chart = new Chart("averageAlertDurationChart", config);
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  calculateAverageTotalDuration(durations: IAverageAlertDurations): number {
    return Object.values(durations).reduce(
      (previous, current) => previous + current,
      0
    );
  }

  ngOnInit(): void {
    this.avgTotalDuration = this.calculateAverageTotalDuration(this.durations);
    // Get translations and draw the chart
    this.translate
      .get([
        "SECONDS",
        "TECHNICAL_DURATION",
        "QUEUE_TIME",
        "RESERVED_TIME",
        "CARE_TIME",
      ])
      .subscribe((t) => {
        this.labels = {
          technicalDurationLabel: t.TECHNICAL_DURATION,
          queueTimeLabel: t.QUEUE_TIME,
          reservedTimeLabel: t.RESERVED_TIME,
          careTimeLabel: t.CARE_TIME,
        };
        this.drawAverageAlertDurationChart();
      });
  }

  async captureForExport(mode: StatisticsExportMode = 'pdf'): Promise<StatisticsExportSnapshot | null> {
    const snapshot = this.buildAvgDurationsSnapshot();
    // Both PDF and PNG draw the stacked bar + KPI cards natively — no html2canvas.
    // PDF embeds the structured layout; the per-widget PNG download rasterizes the
    // same data straight to a canvas.
    if (mode === 'pdf') {
      return snapshot;
    }
    return renderNativeSnapshotToPng(snapshot, (key) => this.translate.instant(key));
  }

  private buildAvgDurationsSnapshot(): StatisticsExportSnapshot {
    const d = this.durations;
    const fmt = (seconds: number) => this.helper.convertSecondsToReadableFormat(seconds);

    // Resolve labels via translate.instant() rather than this.labels so the PDF
    // snapshot does not depend on the ngOnInit translate.get() subscription having
    // run (export could be triggered before the chart labels are initialised).
    const segments = [
      { label: this.translate.instant('TECHNICAL_DURATION'), seconds: d.avgTechDurationSeconds, color: '#FF764A' },
      { label: this.translate.instant('QUEUE_TIME'), seconds: d.avgQueueTimeSeconds, color: '#FFDBA0' },
      { label: this.translate.instant('RESERVED_TIME'), seconds: d.avgReservedTimeSeconds, color: '#70D4FC' },
      { label: this.translate.instant('CARE_TIME'), seconds: d.avgCareTimeSeconds, color: '#4BC2BA' },
    ].map((segment) => ({ ...segment, value: fmt(segment.seconds) }));

    return {
      slug: 'average-alert-durations',
      titleKey: 'AVERAGE_ALERT_DURATIONS',
      imageDataUrl: '',
      avgDurations: {
        totalLabel: this.translate.instant('TOTAL_ALERT_TIME'),
        totalValue: fmt(this.avgTotalDuration),
        segments,
      },
    };
  }
}

const Colors = {
  technicalDurationColor: "rgb(255, 118, 74)",
  queueTimeColor: "rgb(255, 219, 160)",
  reservedTimeColor: "rgb(112, 212, 252)",
  careTimeColor: "rgb(75, 194, 186)",
};
